import React, { useState } from "react";
import { Row, Col, Button, Modal, Image, Pagination, Checkbox } from "antd";
import { BsChevronCompactDown, BsChevronCompactRight } from "react-icons/bs";
import {
  restoreGeneration,
  deleteGeneration,
} from "@api/copilots/browser-extension/generation";
import { SuccessNotification, ErrorNotification } from "..";
import logo from "@assets/images/logo/logo-light.png";
import { useTranslation } from "react-i18next";

const GenerationsTrashTable = ({
  page,
  setPage,
  perPage,
  totalGenerations,
  totalPages,
  generations,
  handleFetchHistoryPaginated,
  selectedIds,
  setSelectedIds,
}) => {
  const { t } = useTranslation();
  const [expandedRow, setExpandedRow] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Handle generation restore
  const handleGenerationRestore = (id) => {
    // Confirm delete
    Modal.confirm({
      title: "Are you sure you want to restore this generation?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          // Archive event
          await restoreGeneration(id);
          // Fetch all admin generations
          handleFetchHistoryPaginated(page);
          // Show success notification
          SuccessNotification("Generation restored successfully");
        } catch (error) {
          // Show error notification
          ErrorNotification("Error restoring generation");
        }
      },
    });
  };

  // Handle generation delete
  const handleGenerationDelete = (id) => {
    // Confirm delete
    Modal.confirm({
      title: "Are you sure you want to permanently delete this generation?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          // Delete event
          await deleteGeneration(id);

          // Fetch all admin generations
          handleFetchHistoryPaginated(page);
          setDeleteLoading(false);

          // Show success notification
          SuccessNotification("Generation deleted successfully");
        } catch (error) {
          setDeleteLoading(false);

          // Show error notification
          ErrorNotification("Error deleting generation");
        }
      },
    });
  };

  const dataSource = generations?.map((generation) => ({
    _id: generation._id,
    input: generation.input,
    category: generation.category,
    copilot: generation.copilot,
    tokens_count: generation.tokens_count,
    createdAt: generation.createdAt,
  }));

  const getTime = (date) => {
    const date1 = date.split("T")[1].split(".")[0];
    return date1;
  };

  const getDate = (date) => {
    const date1 = date.split("T")[0];
    return date1;
  };

  const handleRowClick = (id) => {
    if (expandedRow === id) {
      setExpandedRow(null);
    } else {
      setExpandedRow(id);
    }
  };

  const handleCheckboxChange = (checked, id) => {
    if (checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter((selectedId) => selectedId !== id));
    }
  };

  return (
    <>
      <Row gutter={[0, 16]} className="hp-mt-24">
        <Col span={24}>
          <table
            style={{ width: "100%", height: "100%" }}
            className="custom-table"
          >
            <tbody>
              {dataSource.map((record) => (
                <React.Fragment key={record._id}>
                  <tr key={record._id} className="custom-table-first-row">
                    <td className="custom-table-checkbox">
                      <Checkbox
                        checked={selectedIds.includes(record._id)}
                        onChange={(e) =>
                          handleCheckboxChange(e.target.checked, record._id)
                        }
                      />
                    </td>
                    <td className="custom-table-icon">
                      <div>
                        <Image
                          src={
                            record?.copilot?.image
                              ? record?.copilot?.image
                              : logo
                          }
                          width={50}
                          height={50}
                          preview={false}
                        />
                      </div>
                    </td>
                    <td
                      style={{ width: "70%" }}
                      className="custom-table-first-row-td"
                      onClick={() => handleRowClick(record?._id)}
                    >
                      <div className="inputBox">
                        <div>
                          <h5 className="inputText capitalize-first-letter">
                            {record.input?.substring(0, 25) + "..."}
                          </h5>
                          <div className="inputDateAndTime">
                            <div className="inputDate">
                              {getDate(record.createdAt)}
                            </div>
                            <div className="inputTime">
                              {getTime(record.createdAt)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td
                      className="custom-table-dropdown"
                      onClick={() => handleRowClick(record?._id)}
                    >
                      <div className="expand-icon">
                        {expandedRow === record._id ? (
                          <BsChevronCompactRight className="right-icon" />
                        ) : (
                          <BsChevronCompactDown className="down-icon" />
                        )}
                      </div>
                    </td>
                    <td className="custom-table-second-row-td">
                      <div style={{ display: "flex" }}>
                        <Button
                          size="small"
                          onClick={() => {
                            handleGenerationRestore(record._id);
                          }}
                        >
                          {t("generationTrashList.restore")}
                        </Button>

                        <Button
                          type="danger"
                          size="small"
                          onClick={() => {
                            handleGenerationDelete(record._id);
                          }}
                          loading={deleteLoading}
                          style={{ marginLeft: "8px" }}
                        >
                          {t("generationTrashList.delete")}
                        </Button>
                      </div>
                    </td>
                  </tr>

                  {expandedRow === record._id && (
                    <tr className="custom-table-expanded-row" id="expandedRow">
                      <td colSpan={6}>
                        <div
                          className={`container custom-table-expanded-content ${
                            expandedRow === record._id ? "show" : ""
                          }`}
                        >
                          <Row
                            gutter={16}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <Col
                              className="gutter-row detailsBox expandedTextBox"
                              span={10}
                              offset={2}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  wordBreak: "break-all",
                                  flexWrap: "wrap",
                                  maxWidth: "90%",
                                }}
                              >
                                <h5 className="inputTextTitle">
                                  {" "}
                                  {t("generationTrashList.input")}
                                </h5>
                                <p className="inputTextPara">
                                  {record?.input?.substring(0, 200) + "..."}
                                </p>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  wordBreak: "break-all",
                                  flexWrap: "wrap",
                                  maxWidth: "90%",
                                }}
                              >
                                <h5 className="inputTextTitle">
                                  {" "}
                                  {t("generationTrashList.category")}
                                </h5>
                                <p className="inputTextPara">
                                  {record?.category?.name}
                                </p>
                              </div>
                              <div style={{ display: "flex" }}>
                                <h5 className="inputTextTitle">
                                  {t("generationTrashList.copilot")}
                                </h5>
                                <p className="inputTextPara">
                                  {record?.copilot?.name}
                                </p>
                              </div>
                              <div style={{ display: "flex" }}>
                                <h5 className="inputTextTitle">
                                  {t("generationTrashList.tokensCount")}
                                </h5>
                                <p className="inputTextPara">
                                  {record.tokens_count}
                                </p>
                              </div>
                            </Col>

                            <Col span={12}></Col>
                          </Row>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </Col>
        {/* Pagination */}
        <Col span={24}>
          <Pagination
            current={page}
            pageSize={perPage}
            total={totalGenerations}
            onChange={(page) => {
              setPage(page);
              handleFetchHistoryPaginated(page);
            }}
            showSizeChanger={false}
          />
        </Col>
      </Row>
    </>
  );
};

export default GenerationsTrashTable;
